import { IconX } from "@tabler/icons-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import AddStaffModal from "./AddStaffModal";
import { useRolesData, useStaffCreateMutation } from "../../../apis/staffApis";
import { useState } from "react";
import { useLanguage } from "../../../context/LanguageContext";

interface Props {
  onClose: () => void;
}

export default function AddStaffSidebar({ onClose }: Props) {
  const { t } = useLanguage();
  const { data: roles, isPending: loadingRoles } = useRolesData();

  const { mutate, isPending } = useStaffCreateMutation();

  const [allIds, setAllIds] = useState<any>();

  const getIds = (data: any) => {
    setAllIds(data);
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    control,
    reset,
    formState: { errors },
  } = useForm();

  const handleStaffCreate = (data: any) => {
    const mergedWarehouses: string[] = [];

    if (data.warehouse) {
      mergedWarehouses.push(data.warehouse);
    }

    if (Array.isArray(data.assignWarehouse)) {
      mergedWarehouses.push(
        ...data.assignWarehouse
          .map((warehouse: any) => warehouse?.id)
          .filter(Boolean),
      );
    } else if (data.assignWarehouse?.id) {
      mergedWarehouses.push(data.assignWarehouse.id);
    }

    const hasAnyId =
      allIds &&
      Object.values(allIds).some(
        (v) => v !== null && v !== undefined && v !== "",
      );

    let payload: any = {
      ...data,
      type: "LM",
      ...(hasAnyId ? allIds : {}),
      warehouses: mergedWarehouses,
    };

    delete payload.hub;
    delete payload.assignHub;

    payload = Object.fromEntries(
      Object.entries(payload).filter(([_, value]) => value !== undefined),
    );

    console.log("CREATE STAFF PAYLOAD", payload);

    mutate(payload, {
      onSuccess: () => {
        toast.success(t("Staff Added Successfully."));
        onClose();
        reset();
      },
      onError: (error: any) => {
        console.log("CREATE STAFF ERROR", error);

        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong";

        toast.error(t(errorMessage));
      },
    });
  };

  if (loadingRoles) {
    return (
      <div className="relative rounded-3xl shadow-sm border flex flex-col overflow-hidden bg-white p-6 items-center justify-center min-h-[300px] text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FBD037]"></div>
        <p className="mt-2 text-sm text-slate-500 font-medium">{t("Loading roles...")}</p>
      </div>
    );
  }

  if (!roles?.roles || roles.roles.length === 0) {
    return (
      <div className="relative rounded-3xl shadow-sm border flex flex-col overflow-hidden bg-white p-6 items-center justify-center min-h-[300px] text-center space-y-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg border border-[#D2D2D2]"
        >
          <IconX size={20} />
        </button>
        <div className="bg-[#FEF1CF] text-[#FCB910] p-3 rounded-full">
          <svg className="w-8.5 h-8.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-[#212121]">{t("No Roles Available")}</h3>
        <p className="text-sm text-slate-500 max-w-xs leading-relaxed">
          {t("You must create at least one staff role in the Access Control section before you can add a staff member.")}
        </p>
      </div>
    );
  }

  return (
    <div className="relative rounded-3xl shadow-sm border flex flex-col overflow-hidden bg-white/20">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 ">
        <h2 className="text-xl font-semibold text-[#212121]">{t("Add Staff")}</h2>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSubmit(handleStaffCreate)}
            disabled={isPending}
            className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-lg"
          >
            {isPending ? t("Adding...") : t("Add")}
          </button>

          <button
            onClick={onClose}
            className="p-2 rounded-lg border border-[#D2D2D2]"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      <AddStaffModal
        register={register}
        errors={errors}
        setValue={setValue}
        watch={watch}
        clearErrors={clearErrors}
        control={control}
        getIds={getIds}
      />
    </div>
  );
}
